import i18next from 'i18next';
import moment, {type Moment} from 'moment-timezone';
import React, {useMemo} from 'react';

import {useGenericDialog} from '@famly/mf_modals-dialogs_context';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import type {AutomaticPricing} from 'web-app/react/profile/child-profile/plans-and-invoices/ad-hoc-purchases/hooks/use-get-automatic-pricing-for-purchases';
import {captureMessage} from 'web-app/services/sentry';
import * as Warn from 'web-app/util/warnable';
import {type Warnable} from 'web-app/util/warnable';
import {checkIfFlagIsToggled, DebugLocalStorageFlags} from 'web-app/react/routes/debugger/local-storage-config';

import {Modal} from '../components/modal';
import {PlanModal} from '../components/plan-modal';
import {StyledLoadingOverlay} from '../components/styled-loading-overlay';
import {biweeklyBillingStartDate} from '../helpers/create-billing-periods';
import {
    generatePlanEndDateOptions,
    generatePlanStartDateOptions,
    getClosestPeriodStartDate,
} from '../helpers/generate-edit-plan-date-range-options';
import {useHandlers} from '../hooks/use-handlers';
import {usePermissions} from '../hooks/use-permissions';
import {usePricingGroupsForAdhocPurchase} from '../hooks/use-pricing-group-for-adhoc-purchase';
import {useSharedSelectOptions} from '../hooks/use-select-options';
import {useSimplifiedUsBillingPlanData} from '../hooks/use-simplified-us-billing-plan-data';
import {useSimplifiedUsBillingPlanState} from '../hooks/use-simplified-us-billing-plan-state';
import {useSubmission} from '../hooks/use-submission';
import {useSubmitNewItem} from '../hooks/use-submit-new-item';
import type * as Types from '../types';
import {FormContent} from './form-content';
import {useDeletePlan} from './hooks/use-delete-plan';
import {useEditBillingPlanFormContent} from './hooks/use-edit-billing-plan-form-content';
import {usePlanOverview} from '../hooks/use-plan-overview';

type Props = {
    onClose: VoidFunction;
    afterSubmission?: Types.AfterSubmission;
    handleLegacyEditPlanModalOpen: VoidFunction;
    siteId: string;
    automaticRuleGroupId: string | null;
    automaticPricings: Array<AutomaticPricing>;
    initialPlan: Types.Plan;
    initialStartDate: Types.InitialStartDate;
    firstUnbilledDate: Moment | null;
    selectedChild: Types.Child;
    customDiscounts: Types.DiscountCustom[];
};

const Loader = ({
    onClose,
    afterSubmission,
    handleLegacyEditPlanModalOpen,
    siteId,
    automaticRuleGroupId,
    automaticPricings,
    initialPlan,
    initialStartDate,
    firstUnbilledDate,
    selectedChild,
    customDiscounts,
}: Props) => {
    const shouldFetchPlanOverview =
        initialPlan.type === 'plan' && checkIfFlagIsToggled(DebugLocalStorageFlags.PLAN_OVERVIEW_IN_CHILL_BILL.flag);
    const {planOverview, isLoading: isPlanOverviewLoading} = usePlanOverview({
        siteId,
        childIds: shouldFetchPlanOverview ? [selectedChild.id] : [],
        selectedPlanId: shouldFetchPlanOverview ? initialPlan.id : undefined,
    });

    const simplifiedUsBillingPlan = dispatchWarningsToSentry(
        'Issues with useSimplifiedUsBillingPlanData',
        useSimplifiedUsBillingPlanData({
            siteId,
            initialTuitions: initialPlan.tuitions.map(tuition => tuition.id),
            initialFees: initialPlan.fees,
            customDiscounts,
            startDate: initialStartDate.date,
        }),
    );

    const {showDialog: showDeletePlanDialog, hideDialog: hideDeletePlanDialog} = useGenericDialog();

    const {deletePlan} = useDeletePlan({
        planId: initialPlan.id,
        afterDeletion: () => {
            onClose();
            hideDeletePlanDialog();
            afterSubmission?.([selectedChild.id]);
        },
    });

    const handleDeletePlan = () => {
        showDeletePlanDialog({
            title: i18next.t('react.simplifiedUsBillingPlan.translations.json.deletePlan.title'),
            description: i18next.t('react.simplifiedUsBillingPlan.translations.json.deletePlan.description'),
            onConfirm: deletePlan,
            variant: 'critical',
            confirmText: i18next.t('react.simplifiedUsBillingPlan.translations.json.deletePlan.delete'),
        });
    };

    const biweeklyCadenceStartDate = biweeklyBillingStartDate({
        billingFrequencies: simplifiedUsBillingPlan.billingFrequencies,
    });

    const {canOverridePrice, canAddNewDiscount, canAddNewItem} = usePermissions(siteId);

    const {state, dispatch} = useSimplifiedUsBillingPlanState({
        simplifiedUsBillingPlan,
        initiallySelectedChildIds: [selectedChild.id],
        allChildren: [selectedChild],
        view: 'edit',
        initialPlan,
        initialStartDate,
        biweeklyCadenceStartDate,
    });
    const {
        childrenState: {selectedChildren},
        pricingGroupState: {selectedPricingGroup},
        customPeriodState: {customPeriodDates},
        endDateState: {selectedEndDate},
        displayedModal,
        pricingGroupState,
        planDateRange,
        childrenErrors,
    } = useMemo(() => state, [state]);
    const selectOptions = useSharedSelectOptions({
        state,
        permissions: {
            canAddNewDiscount,
            canAddNewItem,
            isAutomaticDecouplingEnabled: simplifiedUsBillingPlan.isAutomaticDecouplingEnabled,
        },
        pricingGroups: simplifiedUsBillingPlan.pricingGroups,
    });
    const handlers = useHandlers({
        dispatch,
        state,
        billingFrequencies: simplifiedUsBillingPlan.billingFrequencies,
        pricingGroups: simplifiedUsBillingPlan.pricingGroups,
    });

    const initialBillingFrequency = dispatchWarningsToSentry(
        'Issues setting a billing frequency',
        state.billingFrequencyState.initialBillingFrequency,
    );

    const planStartDateOptions = useMemo(() => {
        if (!hasValue(planDateRange)) {
            return [];
        }
        const endDate = planDateRange.endDate;
        return generatePlanStartDateOptions({
            optionsDateRange: {
                startDate: initialStartDate,
                endDate: endDate ? moment(endDate) : null,
            },
            billingFrequency: initialBillingFrequency,
            biweeklyCadenceStartDate,
            planStartDate: moment(
                initialStartDate.type === 'current-plan-effective-date' ? initialStartDate.date : initialPlan.startDate,
            ),
        });
    }, [planDateRange, initialStartDate, initialPlan.startDate, initialBillingFrequency, biweeklyCadenceStartDate]);

    const planEndDateOptions = useMemo(() => {
        if (!hasValue(planDateRange)) {
            return [];
        }
        const startDate = planDateRange.startDate;

        const closestPeriodStartDate = getClosestPeriodStartDate(
            moment(startDate),
            initialBillingFrequency,
            biweeklyCadenceStartDate,
        );

        const defaultEndDate = (() => {
            if (initialBillingFrequency === 'biweekly') {
                return closestPeriodStartDate.moment.clone().add(1, 'weeks').endOf('isoWeek');
            }
            return closestPeriodStartDate.moment
                .clone()
                .endOf(initialBillingFrequency === 'monthly' ? 'month' : 'isoWeek');
        })();

        return generatePlanEndDateOptions({
            dateRange: {
                startDate: moment(startDate),
                endDate: initialPlan.endDate ? moment(initialPlan.endDate) : defaultEndDate,
            },
            billingFrequency: initialBillingFrequency,
            biweeklyCadenceStartDate,
        });
    }, [planDateRange, initialBillingFrequency, initialPlan.endDate, biweeklyCadenceStartDate]);

    const openLegacyPlanEditor = () => {
        handleLegacyEditPlanModalOpen();
        onClose();
    };

    const isPlanFullyBilled = useMemo(() => {
        return childrenErrors.some(error => error.type === 'full-plan-billed');
    }, [childrenErrors]);

    const {
        tuitionSelect,
        pricingGroupSelect,
        discountSelect,
        feeSelect,
        billingFrequencySelect,
        planStartAndEndDateBanner,
        summary,
        childrenErrors: childrenErrorNotices,
        deleteButton,
        fallbackButtonToLegacyPlanEditor,
        planOverview: planOverviewComponent,
        unsupportedPlanNotice,
    } = useEditBillingPlanFormContent({
        handlers: {
            ...handlers,
            handleLegacyEditPlanModalOpen: openLegacyPlanEditor,
            handleDeletePlan,
        },
        state,
        permissions: {
            canOverridePrice,
            isAutomaticDecouplingEnabled: simplifiedUsBillingPlan.isAutomaticDecouplingEnabled,
        },
        selectOptions: {
            ...selectOptions,
            planDateRangeOptions: {
                startDateOptions: planStartDateOptions,
                endDateOptions: planEndDateOptions,
            },
        },
        initialStartDate,
        firstUnbilledDate,
        initialBillingFrequency,
        billingFrequencyConfig: simplifiedUsBillingPlan.billingFrequencies,
        isPlanFullyBilled,
        planOverview: planOverview ?? null,
        siteId,
    });

    const adhocPurchasePricingGroups = usePricingGroupsForAdhocPurchase({
        selectedChildIds: selectedChildren.map(child => child.id),
        selectedPricingGroupId: selectedPricingGroup,
        customPeriodStartDate: customPeriodDates?.startDate ?? null,
        customPeriodEndDate: selectedEndDate,
        automaticRuleGroupId,
        automaticPricings,
    });

    const {handleSubmit, handleSavePlan, isSavePlanDisabled, isSubmissionLoading} = useSubmission({
        trackSubmission: null,
        afterSubmission: (updatedChildIds: Array<Types.Child['id']>) => {
            onClose();
            afterSubmission?.(updatedChildIds);
        },
        setUpBillingFrequency: handlers.setUpBillingFrequency,
        setUpEmptyPlan: handlers.setUpEmptyPlan,
        state,
        siteId,
        billingProfiles: simplifiedUsBillingPlan.billingProfiles,
        pricingGroups: selectOptions.pricingGroupOptions,
        adhocPurchasePricingGroups,
        handleUpdatePlanValidationErrors: handlers.handleUpdatePlanValidationErrors,
        automaticRuleGroupId,
        initialPlan,
    });

    const submitNewItem = useSubmitNewItem({
        billingProfiles: simplifiedUsBillingPlan.billingProfiles,
        handleAllTuitionsUpdate: handlers.handleAllTuitionsUpdate,
        handleAllFeesUpdate: handlers.handleAllFeesUpdate,
        handleModalClose: handlers.handleModalClose,
        pricingGroupState,
        displayedModal,
        siteId,
        allChildren: [selectedChild],
    });

    return (
        <StyledLoadingOverlay loading={simplifiedUsBillingPlan.isLoading || isPlanOverviewLoading}>
            <Modal
                siteId={siteId}
                state={state}
                handlers={handlers}
                pricingGroupOptions={selectOptions.pricingGroupOptions}
                automaticInvoicing={simplifiedUsBillingPlan.automaticInvoicing}
                submission={{
                    handleSubmit,
                    handleSavePlan,
                    isSubmissionLoading,
                    isSavePlanDisabled,
                }}
                submitNewItem={submitNewItem}
            />
            <PlanModal
                form={
                    <FormContent
                        type={initialPlan.type}
                        planStartAndEndDateBanner={planStartAndEndDateBanner}
                        tuitionSelect={tuitionSelect}
                        pricingGroupSelect={pricingGroupSelect}
                        pricingGroupSelectPlacement={state.pricingGroupState.placement}
                        discountSelect={discountSelect}
                        feeSelect={feeSelect}
                        billingFrequencySelect={billingFrequencySelect}
                        fallbackButtonToLegacyPlanEditor={{
                            placement: isPlanFullyBilled ? 'outside-more-options' : 'inside-more-options',
                            component: fallbackButtonToLegacyPlanEditor,
                        }}
                        unsupportedPlanNotice={unsupportedPlanNotice}
                    />
                }
                onClose={onClose}
                deleteButton={deleteButton}
                saveButton={
                    initialPlan.type === 'plan'
                        ? {
                              onSave: handleSavePlan,
                              isDisabled: isSavePlanDisabled,
                              isLoading: isSubmissionLoading,
                          }
                        : {
                              // eslint-disable-next-line @typescript-eslint/no-empty-function
                              onSave: () => {},
                              isDisabled: true,
                              isLoading: false,
                          }
                }
                onCancel={onClose}
                header={{
                    copy: i18next.t('react.simplifiedUsBillingPlan.translations.json.editBillingPlan'),
                    selectedChild,
                }}
                summary={{
                    summaryTitle: i18next.t('react.simplifiedUsBillingPlan.translations.json.invoiceSummary'),
                    summaryContent: initialPlan.type === 'plan' ? summary : null,
                }}
                childrenErrors={initialPlan.type === 'plan' ? childrenErrorNotices : null}
                planOverview={planOverviewComponent}
            />
        </StyledLoadingOverlay>
    );
};

const dispatchWarningsToSentry = <TV, TW>(title: string, warnable: Warnable<TV, TW>): TV =>
    Warn.dispatchWarnings(warnable, warnings => captureMessage(`${title}: ${Warn.showWarningsListSummary(warnings)}`));

export {Loader as EditBillingPlan};
